import React, { useState, useEffect, useMemo } from 'react';
import axios from 'axios';
import { toast } from 'sonner';
import { Lock, ShieldCheck, LogOut, Download, Mail, Phone, Calendar, MessageSquare, RefreshCw, Search, Loader2, Users, Sparkles, ArrowUpRight, BarChart3, FileText, Copy, CheckCircle2, ChevronDown, Send, AlertTriangle, Activity } from 'lucide-react';
import AnalyticsPanel from '@/components/admin/AnalyticsPanel';
import BookingPanel from '@/components/admin/BookingPanel';
import ExtraBooksPanel from '@/components/admin/ExtraBooksPanel';
import WebinarPanel from '@/components/admin/WebinarPanel';
import CrmStatusBanner from '@/components/admin/CrmStatusBanner';
import DeleteConfirmModal from '@/components/admin/DeleteConfirmModal';
import AuditPanel from '@/components/admin/AuditPanel';
import { EVENTS_PORTAL_URL, EVENTS_PORTAL_ADMIN_URL } from '@/lib/eventsPortal';
import { Trash2 } from 'lucide-react';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;
const TOKEN_STORAGE_KEY = 'pv_admin_token';

const AdminPage = () => {
  const [token, setToken] = useState(() => sessionStorage.getItem(TOKEN_STORAGE_KEY) || '');
  const [draftToken, setDraftToken] = useState('');
  const [authError, setAuthError] = useState('');
  const [tab, setTab] = useState('contacts');
  const [contacts, setContacts] = useState([]);
  const [demos, setDemos] = useState([]);
  const [brochures, setBrochures] = useState([]);
  const [calls, setCalls] = useState([]);
  const [stats, setStats] = useState(null);
  const [loading, setLoading] = useState(false);
  const [search, setSearch] = useState('');
  const [egressIp, setEgressIp] = useState(null);
  const [egressLoading, setEgressLoading] = useState(false);
  const [copied, setCopied] = useState(false);

  // Email diagnostics
  const [emailHealth, setEmailHealth] = useState(null);
  const [emailEvents, setEmailEvents] = useState([]);
  const [emailHealthLoading, setEmailHealthLoading] = useState(false);
  const [testEmailTo, setTestEmailTo] = useState('');
  const [testEmailSending, setTestEmailSending] = useState(false);
  const [testEmailResult, setTestEmailResult] = useState(null);

  // Manual record deletion (with typed confirmation + audit)
  const [deleteTarget, setDeleteTarget] = useState(null);

  const headers = useMemo(() => ({ 'X-Admin-Token': token }), [token]);

  useEffect(() => {
    document.title = 'Prime Vision · Admin';
  }, []);

  useEffect(() => {
    if (token) loadAll();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [token]);

  const loadAll = async () => {
    setLoading(true);
    try {
      const [cRes, dRes, bRes, callRes, sRes] = await Promise.all([
        axios.get(`${API}/contacts`, { headers }),
        axios.get(`${API}/demo-requests`, { headers }),
        axios.get(`${API}/brochure-requests`, { headers }),
        axios.get(`${API}/call-requests`, { headers }),
        axios.get(`${API}/stats`, { headers }),
      ]);
      setContacts(cRes.data || []);
      setDemos(dRes.data || []);
      setBrochures(bRes.data || []);
      setCalls(callRes.data || []);
      setStats(sRes.data || null);
    } catch (e) {
      if (e?.response?.status === 401) {
        setAuthError('Token non valido o scaduto');
        sessionStorage.removeItem(TOKEN_STORAGE_KEY);
        setToken('');
      } else {
        toast.error('Errore nel caricamento dati');
      }
    } finally {
      setLoading(false);
    }
    // Also fetch egress IP for Brevo whitelist
    fetchEgressIp();
    // Also fetch email diagnostics
    fetchEmailDiagnostics();
  };

  const fetchEgressIp = async () => {
    setEgressLoading(true);
    try {
      const r = await axios.get(`${API}/debug/egress-ip`, { headers });
      setEgressIp(r.data?.egress_ip || null);
    } catch (e) {
      setEgressIp(null);
    } finally {
      setEgressLoading(false);
    }
  };

  const fetchEmailDiagnostics = async () => {
    setEmailHealthLoading(true);
    try {
      const [hRes, eRes] = await Promise.all([
        axios.get(`${API}/admin/email-health`, { headers }),
        axios.get(`${API}/admin/email-events?limit=20`, { headers }),
      ]);
      setEmailHealth(hRes.data || null);
      setEmailEvents(eRes.data?.events || []);
    } catch (e) {
      // silent — non-blocking
    } finally {
      setEmailHealthLoading(false);
    }
  };

  const sendTestEmail = async () => {
    if (!testEmailTo || !/^\S+@\S+\.\S+$/.test(testEmailTo)) {
      toast.error('Inserisci un\'email valida');
      return;
    }
    setTestEmailSending(true);
    setTestEmailResult(null);
    try {
      const r = await axios.post(
        `${API}/admin/email-test`,
        { to: testEmailTo, note: 'Test inviato dal pannello admin' },
        { headers }
      );
      setTestEmailResult(r.data);
      if (r.data?.ok) {
        toast.success(`Email test inviata a ${testEmailTo}`);
      } else {
        toast.error('Brevo ha rifiutato l\'invio — controlla il dettaglio');
      }
      fetchEmailDiagnostics();
    } catch (e) {
      const msg = e?.response?.data?.detail || e.message;
      toast.error(`Errore: ${msg}`);
      setTestEmailResult({ ok: false, error: msg });
    } finally {
      setTestEmailSending(false);
    }
  };

  const copyIp = async () => {
    if (!egressIp) return;
    try {
      await navigator.clipboard.writeText(egressIp);
      setCopied(true);
      toast.success(`IP ${egressIp} copiato`);
      setTimeout(() => setCopied(false), 2200);
    } catch {
      toast.error('Impossibile copiare. Selezionalo a mano.');
    }
  };

  const handleLogin = (e) => {
    e.preventDefault();
    if (!draftToken.trim()) return;
    sessionStorage.setItem(TOKEN_STORAGE_KEY, draftToken.trim());
    setToken(draftToken.trim());
    setAuthError('');
  };

  const logout = () => {
    sessionStorage.removeItem(TOKEN_STORAGE_KEY);
    setToken('');
    setContacts([]);
    setDemos([]);
    setBrochures([]);
    setCalls([]);
    setStats(null);
  };

  // ---------- CSV export ----------
  const csvEscape = (v) => {
    if (v === null || v === undefined) return '';
    const s = String(v).replace(/"/g, '""').replace(/\r?\n/g, ' ');
    return /[",;\n]/.test(s) ? `"${s}"` : s;
  };

  const downloadCSV = (rows, filename) => {
    if (!rows || rows.length === 0) {
      toast.warning('Nessun dato da esportare');
      return;
    }
    const keys = Array.from(
      rows.reduce((acc, row) => {
        Object.keys(row).forEach((k) => acc.add(k));
        return acc;
      }, new Set())
    );
    const header = keys.map(csvEscape).join(',');
    const body = rows.map((row) => keys.map((k) => csvEscape(row[k])).join(',')).join('\n');
    const csv = `${header}\n${body}`;
    const blob = new Blob(['\uFEFF' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = filename;
    link.click();
    URL.revokeObjectURL(url);
    toast.success(`Esportati ${rows.length} record · ${filename}`);
  };

  const filteredContacts = useMemo(() => {
    if (!search.trim()) return contacts;
    const q = search.toLowerCase();
    return contacts.filter((c) =>
      [c.first_name, c.last_name, c.email, c.phone, c.message]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [contacts, search]);

  const filteredDemos = useMemo(() => {
    if (!search.trim()) return demos;
    const q = search.toLowerCase();
    return demos.filter((d) =>
      [d.first_name, d.last_name, d.email, d.clinic_name]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [demos, search]);

  const filteredBrochures = useMemo(() => {
    if (!search.trim()) return brochures;
    const q = search.toLowerCase();
    return brochures.filter((b) =>
      [b.first_name, b.last_name, b.email, b.phone]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [brochures, search]);

  const filteredCalls = useMemo(() => {
    if (!search.trim()) return calls;
    const q = search.toLowerCase();
    return calls.filter((c) =>
      [c.first_name, c.last_name, c.email, c.phone, c.clinic_name, c.notes]
        .filter(Boolean)
        .some((v) => String(v).toLowerCase().includes(q))
    );
  }, [calls, search]);

  // ---------- Auth screen ----------
  if (!token) {
    return (
      <div className="min-h-screen bg-[#FAFBFD] flex items-center justify-center px-4 relative overflow-hidden">
        <div className="pv-glow-blob w-[480px] h-[480px] bg-pv-teal/15 top-[-160px] right-[-140px]" />
        <div className="pv-glow-blob w-[420px] h-[420px] bg-pv-orange/10 bottom-[-140px] left-[-140px]" />
        <div className="pv-grid-bg absolute inset-0 opacity-30 [mask-image:radial-gradient(ellipse_at_50%_50%,black_20%,transparent_70%)]" />

        <form
          onSubmit={handleLogin}
          className="relative w-full max-w-md bg-white rounded-3xl border border-slate-200 shadow-[0_30px_80px_-30px_rgba(15,23,42,0.20)] p-8 md:p-10"
        >
          <div className="flex items-center gap-3 mb-6">
            <div className="w-12 h-12 rounded-xl bg-pv-ice border border-pv-teal/30 flex items-center justify-center">
              <Lock className="w-5 h-5 text-pv-teal" />
            </div>
            <div>
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-pv-teal font-bold">Prime Vision · Admin</div>
              <h1 className="font-heading font-extrabold text-2xl text-pv-ink tracking-normal leading-tight mt-0.5">CRM lead</h1>
            </div>
          </div>

          <p className="text-slate-600 text-sm leading-relaxed mb-6">
            Inserisci il token di amministrazione per accedere al pannello lead, demo e statistiche.
          </p>

          <label className="block">
            <span className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 mb-2 font-bold block">Admin token</span>
            <input
              type="password"
              autoFocus
              value={draftToken}
              onChange={(e) => setDraftToken(e.target.value)}
              data-testid="pv-admin-token-input"
              placeholder="••••••••••••••••••"
              className="pv-input font-mono"
            />
          </label>

          {authError && (
            <p className="mt-3 text-sm text-red-500 font-semibold">{authError}</p>
          )}

          <button
            type="submit"
            data-testid="pv-admin-login-btn"
            className="pv-btn-primary mt-6 w-full justify-center"
          >
            <ShieldCheck className="w-4 h-4" />
            Accedi al pannello
          </button>

          <p className="mt-6 text-[12px] text-slate-400 font-mono uppercase tracking-[0.18em] text-center font-semibold">
            Dati protetti · GDPR · solo team Prime Vision
          </p>
        </form>
      </div>
    );
  }

  // ---------- Dashboard ----------
  return (
    <div className="min-h-screen bg-[#FAFBFD]">
      {/* Top bar */}
      <header className="bg-white border-b border-slate-200 sticky top-0 z-20">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between gap-4">
          <a href="/" className="flex items-center gap-3">
            <img src="/assets/prime-vision-logo.png" alt="Prime Vision" className="h-8 w-auto" />
            <span className="hidden sm:inline-flex items-center gap-1.5 px-2.5 py-1 rounded-full bg-pv-ice border border-pv-teal/25 text-[10px] font-mono uppercase tracking-[0.18em] text-pv-teal font-bold">
              <Sparkles className="w-2.5 h-2.5" /> admin
            </span>
          </a>

          <div className="flex items-center gap-2 md:gap-3">
            <button
              onClick={loadAll}
              disabled={loading}
              className="pv-btn-ghost !py-2 !px-4 !text-[12.5px]"
              data-testid="pv-admin-refresh"
            >
              {loading ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
              <span className="hidden sm:inline">Aggiorna</span>
            </button>
            <button
              onClick={logout}
              data-testid="pv-admin-logout"
              className="inline-flex items-center gap-2 px-4 py-2 rounded-full text-[12.5px] font-bold text-slate-600 hover:text-red-500 border border-slate-200 hover:border-red-300 transition-all bg-white"
            >
              <LogOut className="w-3.5 h-3.5" />
              <span className="hidden sm:inline">Esci</span>
            </button>
          </div>
        </div>
      </header>

      <main className="max-w-7xl mx-auto px-6 py-8">
        {/* Brevo egress IP helper card */}
        <div className="mb-6 relative bg-gradient-to-br from-pv-peach/40 to-white border border-pv-orange/30 rounded-2xl p-5 md:p-6 shadow-[0_8px_30px_-18px_rgba(255,107,53,0.30)]" data-testid="pv-admin-egress-card">
          <div className="flex items-start gap-4 flex-wrap">
            <div className="shrink-0 w-11 h-11 rounded-xl bg-white border border-pv-orange/40 flex items-center justify-center">
              <ShieldCheck className="w-5 h-5 text-pv-orange" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-pv-orangeDeep font-bold mb-1">Whitelist IP · Brevo</div>
              <h3 className="font-heading font-extrabold text-pv-ink text-[17px] md:text-[18px] leading-tight">
                IP di questo server (aggiungilo a Brevo per ricevere le mail)
              </h3>
              <p className="text-[12.5px] text-slate-600 mt-1.5 max-w-2xl leading-snug">
                Se la whitelist Brevo è attiva, questo IP deve essere autorizzato. Senza autorizzazione le mail vengono rifiutate e i lead non arrivano. Copia l&apos;IP qui sotto, vai su Brevo e aggiungilo.
              </p>
            </div>
          </div>

          <div className="mt-5 flex flex-col md:flex-row items-stretch md:items-center gap-3">
            <div className="flex items-center bg-white border-2 border-dashed border-pv-orange/40 rounded-xl px-4 py-3 flex-1 min-w-0">
              <span className="font-mono text-[11px] uppercase tracking-[0.18em] text-slate-400 font-bold mr-3 shrink-0">IP →</span>
              <span data-testid="pv-admin-egress-ip" className="font-mono text-[18px] md:text-[20px] font-extrabold text-pv-ink tracking-normal truncate select-all">
                {egressLoading ? <Loader2 className="w-5 h-5 animate-spin inline text-pv-orange" /> : (egressIp || '— non disponibile —')}
              </span>
            </div>
            <button
              onClick={copyIp}
              disabled={!egressIp}
              data-testid="pv-admin-copy-ip"
              className="pv-btn-primary !py-3 !px-5 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed justify-center"
            >
              {copied ? (
                <>
                  <CheckCircle2 className="w-4 h-4" />
                  Copiato!
                </>
              ) : (
                <>
                  <Copy className="w-4 h-4" />
                  Copia IP
                </>
              )}
            </button>
            <a
              href="https://app.brevo.com/security/authorised_ips"
              target="_blank"
              rel="noopener noreferrer"
              data-testid="pv-admin-brevo-link"
              className="pv-btn-ghost !py-3 !px-5 !text-[13px] justify-center"
            >
              Apri Brevo
              <ArrowUpRight className="w-3.5 h-3.5" />
            </a>
          </div>

          {/* Step-by-step (collapsed by default to keep it clean) */}
          <details className="mt-4 group">
            <summary className="cursor-pointer text-[12.5px] font-bold text-slate-600 hover:text-pv-orange transition-colors inline-flex items-center gap-1.5 select-none">
              <ChevronDown className="w-3.5 h-3.5 group-open:rotate-180 transition-transform" />
              Come si aggiunge a Brevo (3 passi)
            </summary>
            <ol className="mt-3 ml-5 list-decimal space-y-1.5 text-[12.5px] text-slate-700">
              <li>Clicca <strong>&quot;Copia IP&quot;</strong> qui sopra → poi <strong>&quot;Apri Brevo&quot;</strong>.</li>
              <li>Su Brevo, nella sezione &quot;Authorised IPs&quot;, verifica che la whitelist sia attiva (toggle ON). Clicca <strong>&quot;Add IP&quot;</strong>, incolla l&apos;IP copiato e salva.</li>
              <li>Torna sul sito, riprova il form brochure o contatti: questa volta le mail arrivano.</li>
            </ol>
          </details>
        </div>

        {/* === EMAIL HEALTH / DIAGNOSTICS CARD === */}
        <div
          className={`mb-6 relative bg-gradient-to-br border rounded-2xl p-5 md:p-6 ${
            emailHealth?.failures > 0
              ? 'from-red-50 to-white border-red-300'
              : 'from-pv-mist to-white border-pv-teal/30'
          }`}
          data-testid="pv-admin-email-diag-card"
        >
          <div className="flex items-start gap-4 flex-wrap">
            <div className={`shrink-0 w-11 h-11 rounded-xl border flex items-center justify-center ${
              emailHealth?.failures > 0 ? 'bg-red-100 border-red-300' : 'bg-white border-pv-teal/40'
            }`}>
              {emailHealth?.failures > 0 ? (
                <AlertTriangle className="w-5 h-5 text-red-600" />
              ) : (
                <Activity className="w-5 h-5 text-pv-teal" />
              )}
            </div>
            <div className="flex-1 min-w-0">
              <div className={`font-mono text-[10px] uppercase tracking-[0.22em] font-bold mb-1 ${
                emailHealth?.failures > 0 ? 'text-red-700' : 'text-pv-tealDeep'
              }`}>
                Diagnostica email · Brevo
              </div>
              <h3 className="font-heading font-extrabold text-pv-ink text-[17px] md:text-[18px] leading-tight">
                Stato invio email (brochure · call · contatti)
              </h3>
              <p className="text-[12.5px] text-slate-600 mt-1.5 max-w-2xl leading-snug">
                Mostra in tempo reale se Brevo sta accettando le email. Se vedi <strong>failures &gt; 0</strong>, l&apos;IP del server non è whitelistato — usa la card arancione qui sopra.
              </p>
            </div>
            <button
              onClick={fetchEmailDiagnostics}
              disabled={emailHealthLoading}
              data-testid="pv-admin-email-refresh"
              className="pv-btn-ghost !py-2 !px-4 !text-[12px] shrink-0"
              title="Aggiorna diagnostica"
            >
              <RefreshCw className={`w-3.5 h-3.5 ${emailHealthLoading ? 'animate-spin' : ''}`} />
              Aggiorna
            </button>
          </div>

          {/* Health summary chips */}
          <div className="mt-5 grid grid-cols-2 md:grid-cols-4 gap-3">
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-slate-400 font-bold">Configurato</div>
              <div className="font-extrabold text-[15px] mt-1" data-testid="pv-admin-email-configured">
                {emailHealth?.configured ? (
                  <span className="text-emerald-600">✓ Sì</span>
                ) : (
                  <span className="text-red-600">✗ No</span>
                )}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-slate-400 font-bold">Eventi totali</div>
              <div className="font-extrabold text-pv-ink text-[18px] mt-1" data-testid="pv-admin-email-total">
                {emailHealth?.total_events ?? '—'}
              </div>
            </div>
            <div className="bg-white border border-slate-200 rounded-xl px-4 py-3">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] text-emerald-600 font-bold">Successi</div>
              <div className="font-extrabold text-emerald-600 text-[18px] mt-1" data-testid="pv-admin-email-ok">
                {emailHealth?.ok ?? '—'}
              </div>
            </div>
            <div className={`bg-white border rounded-xl px-4 py-3 ${
              (emailHealth?.failures ?? 0) > 0 ? 'border-red-300' : 'border-slate-200'
            }`}>
              <div className="font-mono text-[9.5px] uppercase tracking-[0.18em] font-bold text-red-600">Errori</div>
              <div className="font-extrabold text-red-600 text-[18px] mt-1" data-testid="pv-admin-email-fails">
                {emailHealth?.failures ?? '—'}
              </div>
            </div>
          </div>

          {/* Last failure detail — only if there is one */}
          {emailHealth?.last_failure && (
            <div className="mt-4 bg-red-50 border border-red-200 rounded-xl p-4" data-testid="pv-admin-email-last-failure">
              <div className="font-mono text-[10px] uppercase tracking-[0.18em] text-red-700 font-bold mb-2">Ultimo errore</div>
              <div className="text-[13px] text-red-900 mb-1">
                <strong>{emailHealth.last_failure.label}</strong> ·
                status <span className="font-mono font-bold">{emailHealth.last_failure.status}</span> ·
                a <span className="font-mono">{emailHealth.last_failure.to?.join(', ')}</span>
              </div>
              <div className="text-[12px] text-red-800 font-mono leading-relaxed bg-white/50 p-2 rounded border border-red-200 mt-2 break-all">
                {emailHealth.last_failure.error}
              </div>
              <div className="text-[11px] text-red-700 mt-2">
                📅 {emailHealth.last_failure.ts}
              </div>
            </div>
          )}

          {/* Send Test Email */}
          <div className="mt-5 pt-5 border-t border-slate-200">
            <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
              Invia email test
            </div>
            <div className="flex flex-col md:flex-row items-stretch md:items-center gap-3">
              <input
                type="email"
                value={testEmailTo}
                onChange={(e) => setTestEmailTo(e.target.value)}
                placeholder="tua-email@esempio.it"
                className="pv-input flex-1"
                data-testid="pv-admin-email-test-input"
                disabled={testEmailSending}
              />
              <button
                onClick={sendTestEmail}
                disabled={testEmailSending || !testEmailTo}
                data-testid="pv-admin-email-test-send"
                className="pv-btn-primary !py-3 !px-5 !text-[13px] disabled:opacity-50 disabled:cursor-not-allowed justify-center"
              >
                {testEmailSending ? (
                  <>
                    <Loader2 className="w-4 h-4 animate-spin" />
                    Invio…
                  </>
                ) : (
                  <>
                    <Send className="w-4 h-4" />
                    Invia test
                  </>
                )}
              </button>
            </div>
            {testEmailResult && (
              <div
                className={`mt-3 p-3 rounded-xl text-[13px] ${
                  testEmailResult.ok
                    ? 'bg-emerald-50 border border-emerald-300 text-emerald-900'
                    : 'bg-red-50 border border-red-300 text-red-900'
                }`}
                data-testid="pv-admin-email-test-result"
              >
                {testEmailResult.ok ? (
                  <>
                    ✓ Email inviata a <strong>{testEmailResult.to}</strong> ·
                    messageId <span className="font-mono text-[11px]">{testEmailResult.message_id}</span>
                    <div className="text-[11.5px] mt-1 text-emerald-700">
                      Controlla la tua casella (anche spam) entro 1-2 minuti.
                    </div>
                  </>
                ) : (
                  <>
                    ✕ Brevo ha rifiutato l&apos;invio · status <strong>{testEmailResult.status}</strong>
                    <div className="font-mono text-[11px] mt-1.5 bg-white/50 p-2 rounded border border-red-200 break-all">
                      {testEmailResult.error}
                    </div>
                  </>
                )}
              </div>
            )}
          </div>

          {/* Recent events log */}
          {emailEvents.length > 0 && (
            <details className="mt-5">
              <summary className="cursor-pointer text-[12.5px] font-bold text-slate-600 hover:text-pv-teal transition-colors inline-flex items-center gap-1.5 select-none">
                <ChevronDown className="w-3.5 h-3.5" />
                Mostra ultimi {emailEvents.length} eventi
              </summary>
              <div className="mt-3 max-h-64 overflow-auto bg-white/50 rounded-xl border border-slate-200">
                <table className="w-full text-[12px]">
                  <thead className="sticky top-0 bg-slate-50 border-b border-slate-200">
                    <tr className="text-left text-[10.5px] font-mono uppercase tracking-wider text-slate-500">
                      <th className="px-3 py-2">Quando</th>
                      <th className="px-3 py-2">Tipo</th>
                      <th className="px-3 py-2">Status</th>
                      <th className="px-3 py-2">Destinatario</th>
                    </tr>
                  </thead>
                  <tbody>
                    {emailEvents.map((ev, i) => (
                      <tr key={i} className={`border-b border-slate-100 ${!ev.ok ? 'bg-red-50/50' : ''}`}>
                        <td className="px-3 py-2 font-mono text-[10.5px] text-slate-500 whitespace-nowrap">
                          {new Date(ev.ts).toLocaleString('it-IT', { hour: '2-digit', minute: '2-digit', day: '2-digit', month: '2-digit' })}
                        </td>
                        <td className="px-3 py-2 font-bold text-pv-ink">{ev.label}</td>
                        <td className={`px-3 py-2 font-mono font-bold ${ev.ok ? 'text-emerald-600' : 'text-red-600'}`}>
                          {ev.ok ? `✓ ${ev.status}` : `✕ ${ev.status || 'NET'}`}
                        </td>
                        <td className="px-3 py-2 font-mono text-[10.5px] text-slate-700 truncate max-w-xs">
                          {ev.to?.join(', ')}
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </details>
          )}
        </div>

        {/* CRM principale — Google Sheet status */}
        <CrmStatusBanner token={token} />

        {/* Stats KPI strip */}
        {stats && (
          <div className="grid grid-cols-2 lg:grid-cols-5 gap-4 md:gap-5 mb-8">
            <StatCard icon={Phone} label="Richieste call" value={stats.call_requests || 0} color="teal" testId="pv-admin-stat-calls" highlight />
            <StatCard icon={Users} label="Lead form" value={stats.contacts} color="teal" testId="pv-admin-stat-contacts" />
            <StatCard icon={Sparkles} label="Richieste demo" value={stats.demo_requests} color="orange" testId="pv-admin-stat-demos" />
            <StatCard icon={FileText} label="Brochure scaricate" value={stats.brochure_requests || 0} color="orange" testId="pv-admin-stat-brochures" />
            <StatCard icon={BarChart3} label="Totale" value={(stats.contacts || 0) + (stats.demo_requests || 0) + (stats.brochure_requests || 0) + (stats.call_requests || 0)} color="slate" testId="pv-admin-stat-total" />
          </div>
        )}

        {/* Tabs */}
        <div className="flex items-center justify-between gap-3 flex-wrap mb-6">
          <div className="inline-flex bg-white border border-slate-200 rounded-full p-1 shadow-[0_4px_12px_-6px_rgba(15,23,42,0.10)] flex-wrap">
            <button
              onClick={() => setTab('calls')}
              data-testid="pv-admin-tab-calls"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'calls' ? 'bg-pv-teal text-white shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]' : 'text-slate-600 hover:text-pv-teal'
              }`}
            >
              <Phone className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Call ({calls.length})
            </button>
            <button
              onClick={() => setTab('contacts')}
              data-testid="pv-admin-tab-contacts"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'contacts' ? 'bg-pv-teal text-white shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]' : 'text-slate-600 hover:text-pv-teal'
              }`}
            >
              <Users className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Lead form ({contacts.length})
            </button>
            <button
              onClick={() => setTab('demos')}
              data-testid="pv-admin-tab-demos"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'demos' ? 'bg-pv-teal text-white shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]' : 'text-slate-600 hover:text-pv-teal'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Demo ({demos.length})
            </button>
            <button
              onClick={() => setTab('brochures')}
              data-testid="pv-admin-tab-brochures"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'brochures' ? 'bg-pv-orange text-white shadow-[0_4px_12px_-4px_rgba(255,107,53,0.5)]' : 'text-slate-600 hover:text-pv-orange'
              }`}
            >
              <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Brochure ({brochures.length})
            </button>
            <button
              onClick={() => setTab('webinar')}
              data-testid="pv-admin-tab-webinar"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'webinar' ? 'bg-violet-600 text-white shadow-[0_4px_12px_-4px_rgba(124,58,237,0.5)]' : 'text-slate-600 hover:text-violet-600'
              }`}
            >
              <Sparkles className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Eventi
            </button>
            <button
              onClick={() => setTab('libri')}
              data-testid="pv-admin-tab-libri"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'libri' ? 'bg-pv-teal text-white shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]' : 'text-slate-600 hover:text-pv-teal'
              }`}
            >
              <FileText className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Libri
            </button>
            <button
              onClick={() => setTab('analytics')}
              data-testid="pv-admin-tab-analytics"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'analytics' ? 'bg-pv-ink text-white shadow-[0_4px_12px_-4px_rgba(11,18,32,0.5)]' : 'text-slate-600 hover:text-pv-ink'
              }`}
            >
              <BarChart3 className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Analytics
            </button>
            <button
              onClick={() => setTab('booking')}
              data-testid="pv-admin-tab-booking"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'booking' ? 'bg-emerald-600 text-white shadow-[0_4px_12px_-4px_rgba(16,185,129,0.45)]' : 'text-slate-600 hover:text-emerald-600'
              }`}
            >
              <Calendar className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Calendario
            </button>
            <button
              onClick={() => setTab('audit')}
              data-testid="pv-admin-tab-audit"
              className={`px-4 py-2 rounded-full text-[13px] font-bold transition-all ${
                tab === 'audit' ? 'bg-slate-700 text-white shadow-[0_4px_12px_-4px_rgba(51,65,85,0.5)]' : 'text-slate-600 hover:text-slate-800'
              }`}
            >
              <ShieldCheck className="w-3.5 h-3.5 inline mr-1.5 -mt-0.5" />
              Audit
            </button>
          </div>

          {tab !== 'libri' && (
          <div className="flex items-center gap-2 flex-wrap">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-slate-400" />
              <input
                type="text"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Cerca nome, email…"
                data-testid="pv-admin-search"
                className="pl-9 pr-3 py-2 rounded-full bg-white border border-slate-200 text-[13px] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:border-pv-teal/60 transition-colors w-56"
              />
            </div>
            <button
              onClick={() => {
                const date = new Date().toISOString().slice(0, 10);
                if (tab === 'contacts') return downloadCSV(filteredContacts, `prime-vision-leads-${date}.csv`);
                if (tab === 'demos') return downloadCSV(filteredDemos, `prime-vision-demos-${date}.csv`);
                if (tab === 'calls') return downloadCSV(filteredCalls, `prime-vision-calls-${date}.csv`);
                return downloadCSV(filteredBrochures, `prime-vision-brochures-${date}.csv`);
              }}
              data-testid="pv-admin-export-csv"
              className="pv-btn-primary !py-2 !px-4 !text-[12.5px]"
            >
              <Download className="w-3.5 h-3.5" />
              Esporta CSV
            </button>
          </div>
          )}
        </div>

        {/* List */}
        {loading && contacts.length === 0 && (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-6 h-6 text-pv-teal animate-spin" />
          </div>
        )}

        {tab === 'contacts' && (
          <div className="space-y-3">
            {filteredContacts.length === 0 && !loading && (
              <EmptyState label="Nessun lead da mostrare" />
            )}
            {filteredContacts.map((lead) => (
              <ContactRow
                key={lead.id}
                lead={lead}
                onDelete={() => setDeleteTarget({ collection: 'contact_leads', collectionLabel: 'CRM · Lead', id: lead.id, label: `${lead.first_name || ''} ${lead.last_name || ''} · ${lead.email}`.trim() })}
              />
            ))}
          </div>
        )}

        {tab === 'calls' && (
          <div className="space-y-3">
            {filteredCalls.length === 0 && !loading && (
              <EmptyState label="Nessuna richiesta di call ancora" />
            )}
            {filteredCalls.map((c) => (
              <CallRow
                key={c.id}
                call={c}
                onDelete={() => setDeleteTarget({ collection: 'call_requests', collectionLabel: 'Call', id: c.id, label: `${c.first_name || ''} ${c.last_name || ''} · ${c.email}`.trim() })}
              />
            ))}
          </div>
        )}

        {tab === 'demos' && (
          <div className="space-y-3">
            {filteredDemos.length === 0 && !loading && (
              <EmptyState label="Nessuna richiesta demo" />
            )}
            {filteredDemos.map((d) => (
              <DemoRow
                key={d.id}
                demo={d}
                onDelete={() => setDeleteTarget({ collection: 'demo_requests', collectionLabel: 'Demo', id: d.id, label: `${d.first_name || ''} ${d.last_name || ''} · ${d.email}`.trim() })}
              />
            ))}
          </div>
        )}

        {tab === 'brochures' && (
          <div className="space-y-3">
            {filteredBrochures.length === 0 && !loading && (
              <EmptyState label="Nessun download brochure ancora" />
            )}
            {filteredBrochures.map((b) => (
              <BrochureRow
                key={b.id}
                brochure={b}
                onDelete={() => setDeleteTarget({ collection: 'brochure_leads', collectionLabel: 'Brochure/FSE', id: b.id, label: `${b.first_name || ''} ${b.last_name || ''} · ${b.email}`.trim() })}
              />
            ))}
          </div>
        )}

        {tab === 'analytics' && (
          <AnalyticsPanel token={token} />
        )}

        {tab === 'booking' && (
          <BookingPanel token={token} />
        )}

        {tab === 'webinar' && (
          <div className="space-y-5">
            <div
              className="bg-gradient-to-br from-violet-50 to-white border border-violet-200 rounded-2xl p-6"
              data-testid="pv-admin-events-portal-card"
            >
              <div className="font-mono text-[10px] uppercase tracking-[0.22em] text-violet-700 font-bold mb-2">
                Portale eventi unico
              </div>
              <h3 className="font-heading font-extrabold text-pv-ink text-[18px] leading-tight">
                Eventi, iscritti e sync CRM si gestiscono su eventi.primevision.app
              </h3>
              <p className="text-[13px] text-slate-600 mt-2 max-w-2xl leading-relaxed">
                Creazione/modifica eventi, lista partecipanti, reminder e sincronizzazione col CRM DEFINITIVO
                avvengono sul portale eventi esterno (login admin: admin@primevision.it).
              </p>
              <div className="mt-4 flex items-center gap-2 flex-wrap">
                <a
                  href={EVENTS_PORTAL_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pv-admin-events-portal-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-violet-600 hover:bg-violet-700 text-white text-[13px] font-bold transition-colors"
                >
                  <Sparkles className="w-4 h-4" /> Apri portale eventi
                </a>
                <a
                  href={EVENTS_PORTAL_ADMIN_URL}
                  target="_blank"
                  rel="noopener noreferrer"
                  data-testid="pv-admin-events-portal-admin-link"
                  className="inline-flex items-center gap-2 px-5 py-2.5 rounded-xl bg-white border border-violet-300 text-violet-700 hover:bg-violet-50 text-[13px] font-bold transition-colors"
                >
                  <ShieldCheck className="w-4 h-4" /> Admin eventi
                </a>
              </div>
            </div>
            <details className="bg-white border border-slate-200 rounded-2xl p-5" data-testid="pv-admin-webinar-archive">
              <summary className="cursor-pointer font-bold text-[13.5px] text-slate-700 select-none">
                Archivio interno · iscritti webinar storici (sola consultazione)
              </summary>
              <div className="mt-5">
                <WebinarPanel token={token} />
              </div>
            </details>
          </div>
        )}

        {tab === 'libri' && (
          <ExtraBooksPanel token={token} />
        )}

        {tab === 'audit' && (
          <AuditPanel token={token} />
        )}

        <DeleteConfirmModal
          target={deleteTarget}
          token={token}
          onClose={() => setDeleteTarget(null)}
          onDeleted={() => { setDeleteTarget(null); loadAll(); }}
        />
      </main>
    </div>
  );
};

const StatCard = ({ icon: Icon, label, value, color, testId, highlight }) => {
  const colors = {
    teal: 'from-pv-teal/15 to-pv-teal/0 border-pv-teal/20 text-pv-teal',
    orange: 'from-pv-orange/15 to-pv-orange/0 border-pv-orange/20 text-pv-orange',
    slate: 'from-slate-100 to-slate-50 border-slate-200 text-slate-700',
  };
  return (
    <div
      data-testid={testId}
      className={`relative bg-white rounded-2xl p-5 border ${highlight ? 'border-pv-teal/40 shadow-[0_8px_30px_-12px_rgba(27,127,184,0.30)] ring-1 ring-pv-teal/20' : 'border-slate-200 shadow-[0_8px_24px_-16px_rgba(15,23,42,0.10)]'} overflow-hidden`}
    >
      <div className={`absolute -top-12 -right-12 w-32 h-32 rounded-full bg-gradient-radial ${colors[color]} blur-2xl opacity-60`} />
      <div className="flex items-center gap-2 font-mono text-[10px] uppercase tracking-[0.22em] text-slate-500 font-bold mb-3">
        <Icon className={`w-3.5 h-3.5 ${color === 'teal' ? 'text-pv-teal' : color === 'orange' ? 'text-pv-orange' : 'text-slate-500'}`} />
        {label}
      </div>
      <div className="font-heading font-extrabold text-4xl text-pv-ink tracking-normal">{value ?? '—'}</div>
    </div>
  );
};

const DeleteBtn = ({ onClick, testId }) => (
  <button
    onClick={onClick}
    data-testid={testId}
    title="Elimina definitivamente (richiede conferma)"
    className="inline-flex items-center justify-center w-8 h-8 rounded-full bg-white border border-slate-200 text-slate-300 hover:text-red-600 hover:border-red-300 hover:bg-red-50 transition-colors shrink-0"
  >
    <Trash2 className="w-3.5 h-3.5" />
  </button>
);

const ContactRow = ({ lead, onDelete }) => {
  const fullName = `${lead.first_name || ''} ${lead.last_name || ''}`.trim() || lead.email;
  return (
    <article
      data-testid={`pv-admin-contact-${lead.id}`}
      className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-pv-teal/40 transition-colors shadow-[0_4px_16px_-12px_rgba(15,23,42,0.10)]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-pv-ice border border-pv-teal/25 flex items-center justify-center shrink-0">
            <span className="font-heading font-extrabold text-pv-teal text-sm">
              {(lead.first_name?.[0] || '?').toUpperCase()}{(lead.last_name?.[0] || '').toUpperCase()}
            </span>
          </div>
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-pv-ink text-base truncate">{fullName}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-mono uppercase tracking-wider flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(lead.created_at)}</span>
              {lead.locale && <span className="inline-flex items-center gap-1">· {lead.locale.toUpperCase()}</span>}
              {lead.source && <span className="inline-flex items-center gap-1">· {lead.source}</span>}
              {lead.origin_label && <span className="inline-flex items-center gap-1 text-pv-teal font-bold">· {lead.origin_label}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`mailto:${lead.email}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pv-ice border border-pv-teal/20 text-pv-teal text-[12px] font-bold hover:bg-pv-teal hover:text-white transition-colors"
          >
            <Mail className="w-3 h-3" /> {lead.email}
          </a>
          {lead.phone && (
            <a
              href={`tel:${lead.phone}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-[12px] font-bold hover:border-pv-teal/40 hover:text-pv-teal transition-colors"
            >
              <Phone className="w-3 h-3" /> {lead.phone}
            </a>
          )}
          {onDelete && <DeleteBtn onClick={onDelete} testId={`pv-admin-delete-contact-${lead.id}`} />}
        </div>
      </div>

      {(lead.message || lead.birth_date) && (
        <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-12 gap-3">
          {lead.birth_date && (
            <div className="md:col-span-3">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-1">Data nascita</div>
              <div className="text-[13px] text-slate-700 font-semibold">{lead.birth_date}</div>
            </div>
          )}
          {lead.message && (
            <div className="md:col-span-9">
              <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-1 flex items-center gap-1.5">
                <MessageSquare className="w-3 h-3" /> Messaggio
              </div>
              <p className="text-[13px] text-slate-700 leading-snug font-medium whitespace-pre-line">{lead.message}</p>
            </div>
          )}
        </div>
      )}
    </article>
  );
};

const DemoRow = ({ demo, onDelete }) => {  const fullName = `${demo.first_name || ''} ${demo.last_name || ''}`.trim() || demo.email;
  return (
    <article
      data-testid={`pv-admin-demo-${demo.id}`}
      className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-pv-orange/40 transition-colors shadow-[0_4px_16px_-12px_rgba(15,23,42,0.10)]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-pv-peach border border-pv-orange/25 flex items-center justify-center shrink-0">
            <Sparkles className="w-4 h-4 text-pv-orange" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-pv-ink text-base truncate">{fullName}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-mono uppercase tracking-wider flex-wrap">
              <span>{formatDate(demo.created_at)}</span>
              {demo.clinic_name && <span>· {demo.clinic_name}</span>}
              {demo.source && <span>· {demo.source}</span>}
            </div>
          </div>
        </div>
        <a
          href={`mailto:${demo.email}`}
          className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pv-peach border border-pv-orange/20 text-pv-orangeDeep text-[12px] font-bold hover:bg-pv-orange hover:text-white transition-colors"
        >
          <Mail className="w-3 h-3" /> {demo.email}
          <ArrowUpRight className="w-3 h-3" />
        </a>
        {onDelete && <DeleteBtn onClick={onDelete} testId={`pv-admin-delete-demo-${demo.id}`} />}
      </div>
    </article>
  );
};

const EmptyState = ({ label }) => (
  <div className="text-center py-16 bg-white rounded-2xl border border-dashed border-slate-200">
    <div className="inline-flex items-center justify-center w-12 h-12 rounded-xl bg-slate-100 text-slate-400 mb-3">
      <Users className="w-5 h-5" />
    </div>
    <p className="text-slate-500 font-semibold">{label}</p>
  </div>
);

const BrochureRow = ({ brochure, onDelete }) => {  const fullName = `${brochure.first_name || ''} ${brochure.last_name || ''}`.trim() || brochure.email;
  return (
    <article
      data-testid={`pv-admin-brochure-${brochure.id}`}
      className="bg-white rounded-2xl border border-slate-200 p-5 hover:border-pv-orange/40 transition-colors shadow-[0_4px_16px_-12px_rgba(15,23,42,0.10)]"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="relative w-11 h-11 rounded-xl bg-pv-peach border border-pv-orange/25 flex items-center justify-center shrink-0">
            <FileText className="w-4 h-4 text-pv-orange" />
            <span className="absolute -top-1 -right-1 text-[8px] font-mono font-extrabold tracking-wider bg-pv-orange text-white px-1 py-0.5 rounded">PDF</span>
          </div>
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-pv-ink text-base truncate">{fullName}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-mono uppercase tracking-wider flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(brochure.created_at)}</span>
              {brochure.locale && <span>· {brochure.locale.toUpperCase()}</span>}
              {brochure.source && <span>· {brochure.source}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`mailto:${brochure.email}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-pv-peach border border-pv-orange/20 text-pv-orangeDeep text-[12px] font-bold hover:bg-pv-orange hover:text-white transition-colors"
          >
            <Mail className="w-3 h-3" /> {brochure.email}
          </a>
          {brochure.phone && (
            <a
              href={`tel:${brochure.phone}`}
              className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-[12px] font-bold hover:border-pv-orange/40 hover:text-pv-orange transition-colors"
            >
              <Phone className="w-3 h-3" /> {brochure.phone}
            </a>
          )}
          {onDelete && <DeleteBtn onClick={onDelete} testId={`pv-admin-delete-brochure-${brochure.id}`} />}
        </div>
      </div>
      {brochure.birth_date && (
        <div className="mt-3 pt-3 border-t border-slate-100">
          <span className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-slate-400 font-bold">Data nascita: </span>
          <span className="text-[13px] text-slate-700 font-semibold">{brochure.birth_date}</span>
        </div>
      )}
    </article>
  );
};

const SLOT_LABELS = {
  any: 'Indifferente',
  morning: 'Mattina (9-13)',
  afternoon: 'Pomeriggio (14-18)',
  evening: 'Sera (18-20)',
};
const DAY_LABELS = {
  any: 'Indifferente',
  weekday: 'Giorni feriali',
  weekend: 'Weekend',
};

const CallRow = ({ call, onDelete }) => {
  const fullName = `${call.first_name || ''} ${call.last_name || ''}`.trim() || call.email;
  const dayLabel = call.preferred_day ? (DAY_LABELS[call.preferred_day] || call.preferred_day) : null;
  const slotLabel = call.preferred_slot ? (SLOT_LABELS[call.preferred_slot] || call.preferred_slot) : null;
  return (
    <article
      data-testid={`pv-admin-call-${call.id}`}
      className="bg-white rounded-2xl border-2 border-pv-teal/30 p-5 hover:border-pv-teal/60 transition-colors shadow-[0_8px_24px_-12px_rgba(27,127,184,0.20)] ring-1 ring-pv-teal/10"
    >
      <div className="flex items-start justify-between gap-4 flex-wrap">
        <div className="flex items-center gap-3 flex-1 min-w-0">
          <div className="w-11 h-11 rounded-xl bg-pv-teal/15 border border-pv-teal/30 flex items-center justify-center shrink-0">
            <Phone className="w-5 h-5 text-pv-teal" />
          </div>
          <div className="min-w-0">
            <div className="font-heading font-extrabold text-pv-ink text-base truncate">{fullName}</div>
            <div className="flex items-center gap-3 mt-1 text-xs text-slate-500 font-mono uppercase tracking-wider flex-wrap">
              <span className="inline-flex items-center gap-1"><Calendar className="w-3 h-3" />{formatDate(call.created_at)}</span>
              {call.locale && <span>· {call.locale.toUpperCase()}</span>}
              {call.clinic_name && <span>· {call.clinic_name}</span>}
            </div>
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <a
            href={`tel:${call.phone}`}
            data-testid={`pv-admin-call-tel-${call.id}`}
            className="inline-flex items-center gap-1.5 px-3.5 py-2 rounded-full bg-pv-teal text-white text-[12.5px] font-bold hover:bg-pv-tealDeep transition-colors shadow-[0_4px_12px_-4px_rgba(27,127,184,0.5)]"
          >
            <Phone className="w-3.5 h-3.5" /> {call.phone}
          </a>
          <a
            href={`mailto:${call.email}`}
            className="inline-flex items-center gap-1.5 px-3 py-1.5 rounded-full bg-slate-50 border border-slate-200 text-slate-700 text-[12px] font-bold hover:border-pv-teal/40 hover:text-pv-teal transition-colors"
          >
            <Mail className="w-3 h-3" /> {call.email}
          </a>
          {onDelete && <DeleteBtn onClick={onDelete} testId={`pv-admin-delete-call-${call.id}`} />}
        </div>
      </div>

      <div className="mt-4 pt-4 border-t border-slate-100 grid grid-cols-1 md:grid-cols-12 gap-3">
        {(dayLabel || slotLabel) && (
          <div className="md:col-span-5">
            <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-pv-teal font-bold mb-1 flex items-center gap-1.5">
              <Calendar className="w-3 h-3" /> Quando preferisce
            </div>
            <div className="text-[13px] text-slate-700 font-semibold">
              {[dayLabel, slotLabel].filter(Boolean).join(' · ')}
            </div>
          </div>
        )}
        {call.notes && (
          <div className="md:col-span-7">
            <div className="font-mono text-[9.5px] uppercase tracking-[0.22em] text-slate-400 font-bold mb-1 flex items-center gap-1.5">
              <MessageSquare className="w-3 h-3" /> Note del lead
            </div>
            <p className="text-[13px] text-slate-700 leading-snug font-medium whitespace-pre-line">{call.notes}</p>
          </div>
        )}
      </div>
    </article>
  );
};

const formatDate = (iso) => {
  if (!iso) return '—';
  try {
    const d = new Date(iso);
    return d.toLocaleString('it-IT', { day: '2-digit', month: 'short', year: 'numeric', hour: '2-digit', minute: '2-digit' });
  } catch {
    return iso;
  }
};

export default AdminPage;
